import { useContext, useEffect, useRef, useState } from 'react'
import getItemIds from '@utilities/functions/getItemIds'
import getUpdatedItems from '@utilities/axios/getUpdatedItems'
import sortItems from '@utilities/functions/sortItems'
import { AuthContext } from '../../../../contexts/AuthContext'
import _isEmpty from 'lodash/isEmpty'
import _cloneDeep from 'lodash/cloneDeep'
import hydrateWatchlist from '@utilities/functions/hydrateWatchlist'
import useDidMountEffect from '@utilities/hooks/useDidMountEffect'
import { GlobalUIContext } from '../../../../contexts/GlobalUIContext'
import { buildQueries } from '@components/Items/utils/functions'
import { dataType, ItemQuery } from '@components/Items/types'
import axios, { CancelTokenSource } from 'axios'

type useItemsType = (
  initialData: dataType,
  query: ItemQuery,
  staticQueries?: ItemQuery
) => [dataType & { isLoading: boolean; lastUpdated: Date }, (val: any) => void]

const useItems: useItemsType = (initialData, query, staticQueries = {}) => {
  const sourceRef = useRef<CancelTokenSource | null>(null)
  const { tabVisible } = useContext<any>(GlobalUIContext)
  const {
    userData: { watchlistItemIds },
    isAuthenticated,
    instance,
    status,
  } = useContext<any>(AuthContext)

  const [data, setData] = useState<any>({
    items: initialData.items
      ? hydrateWatchlist(
          sortItems(initialData.items, getItemIds(initialData.items)),
          watchlistItemIds
        )
      : [],
    isLoading: false,
    lastUpdated: new Date(),
    totalCount: initialData.totalCount,
  })

  const initiateItems = async (cancelToken: any) => {
    setData((prevData: any) => ({ ...prevData, isLoading: true }))
    const newUrl = `/search/searchItemList${buildQueries({
      ...query,
      ...staticQueries,
    })}`
    try {
      const { data }: { data: any } = await instance.get(newUrl, {
        cancelToken,
      })
      const { items, totalCount } = data
      if (_isEmpty(items))
        return setData({
          items: [],
          isLoading: false,
          lastUpdated: new Date(),
          totalCount,
        })
      const itemIds = getItemIds(items)
      const newItems = await getUpdatedItems({
        itemIds,
        watchlistItemIds,
        instance,
        cancelToken,
      })
      const hydratedItem = items
        ? hydrateWatchlist(
            newItems ? combineArray(items, newItems) : items,
            watchlistItemIds
          )
        : []

      if (Object.hasOwn(hydratedItem[0], 'currentBid')) {
        setData({
          items: hydratedItem,
          isLoading: false,
          lastUpdated: new Date(),
          totalCount,
        })
      }
    } catch (error) {
      if (axios.isCancel(error)) {
      } else {
        console.error(error)
      }
    }
  }

  useDidMountEffect(() => {
    if (sourceRef.current) {
      sourceRef.current.cancel('Query changed')
    }
    sourceRef.current = axios.CancelToken.source()
    initiateItems(sourceRef.current.token)
  }, [query])

  useEffect(() => {
    if (data.isLoading || !tabVisible || status === 'loading') return
    const itemIds = getItemIds(data.items)
    sourceRef.current = axios.CancelToken.source()

    const interval = setInterval(
      async () => {
        const newItems = await getUpdatedItems({
          itemIds: itemIds,
          watchlistItemIds,
          instance,
          cancelToken: sourceRef.current?.token,
        })
        if (!newItems) return

        setData((prevData: dataType) => ({
          ...prevData,
          isLoading: false,
          lastUpdated: new Date(),
          items: hydrate(newItems, prevData.items),
        }))
      },
      isAuthenticated ? FIVE_SECONDS : FIVE_MINUTES
    )
    return () => clearInterval(interval)
  }, [data.isLoading, isAuthenticated, tabVisible, watchlistItemIds])

  useEffect(() => {
    setData((prevData: dataType) => ({
      ...prevData,
      items: hydrateWatchlist(prevData.items, watchlistItemIds),
      lastUpdated: new Date(),
    }))
  }, [watchlistItemIds])

  return [
    data,
    (val) => {
      let items = sortItems(val, getItemIds(data.items))
      setData(hydrate(items, data))
    },
  ]
}

const hydrate = (newItems: any[] = [], items: any[] = []) => {
  if (!newItems.length) return []

  const newItemsHydrated = []

  for (let newItem of newItems) {
    let item = items.find((item) => item.id === newItem.id)
    if (item) {
      const freshItem = _cloneDeep(item)
      newItemsHydrated.push({ ...freshItem, ...newItem })
    } else {
      newItemsHydrated.push(newItem)
    }
  }
  return newItemsHydrated
}

export const combineArray = (A: any, B: any) => {
  for (let bObj of B) {
    const bId = bObj.id
    let found = false

    for (let i = 0; i < A.length; i++) {
      const aObj = A[i]
      const aId = aObj.id

      if (aId === bId) {
        A[i] = { ...aObj, ...bObj }
        found = true
        break
      }
    }

    if (!found) {
      A.push(bObj)
    }
  }

  return A
}
const FIVE_SECONDS = 5 * 1000
const FIVE_MINUTES = 5 * 60 * 1000

export default useItems
